/**
 * Drives the sitewide coupon-discount-bar section (see sections/coupon-discount-bar.liquid).
 *
 * The bar is server-rendered with real numbers whenever the cart already has items. This script
 * only needs to handle what Liquid can't: reading Shopify's discount_code cookie for the "just
 * landed, cart is still empty" fallback state, dismiss-for-session, and refreshing the content
 * (via a plain /cart.js fetch) whenever the cart changes elsewhere on the page without a reload.
 */
(function () {
  const STORAGE_KEY = 'dovetail:coupon-discount-bar:dismissed';

  function getCookie(name) {
    const value = '; ' + document.cookie;
    const parts = value.split('; ' + name + '=');
    if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    return null;
  }

  function clearCookie(name) {
    document.cookie = `${name}=; Max-Age=0; path=/;`;
  }

  // Shopify/Klaviyo append a random unique-code suffix to per-recipient discount links
  // (e.g. "SMSWELCOME10-SLCRSKSF") -- that suffix isn't meant to be customer-facing.
  function baseCode(code) {
    return (code || '').split('-')[0];
  }

  function formatMoney(cents) {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.shopCurrency || 'USD',
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  function friendlyLabel(discount) {
    if (discount.value_type === 'percentage') {
      return Math.round(parseFloat(discount.value)) + '% off';
    }
    if (discount.value_type === 'fixed_amount') {
      return formatMoney(discount.total_allocated_amount) + ' off';
    }
    return baseCode(discount.title);
  }

  function signatureFor(discounts) {
    return discounts
      .map((d) => d.title)
      .sort()
      .join(',');
  }

  function buildRowsHTML(bar, discounts) {
    const multiDisplay = bar.dataset.multiDisplay || 'stack';
    const suffix = bar.dataset.suffix || '';
    const showCode = bar.dataset.showCode === 'true';

    if (multiDisplay === 'combine' && discounts.length > 1) {
      const labels = discounts.map(friendlyLabel).join(' + ');
      return `<span class="coupon-discount-bar__row"><span aria-hidden="true">&#10003;</span>${escapeHTML(labels)} ${escapeHTML(suffix)}</span>`;
    }

    if (discounts.length === 1) {
      const discount = discounts[0];
      const code = showCode ? `<span class="coupon-discount-bar__code">${escapeHTML(baseCode(discount.title))}</span>` : '';
      return `<span class="coupon-discount-bar__row"><span aria-hidden="true">&#10003;</span>${escapeHTML(friendlyLabel(discount))} ${escapeHTML(suffix)}${code}</span>`;
    }

    return discounts
      .map((discount) => {
        const code = showCode ? `<span class="coupon-discount-bar__code">${escapeHTML(baseCode(discount.title))}</span>` : '';
        const saved = `<span class="coupon-discount-bar__saved">-${formatMoney(discount.total_allocated_amount)}</span>`;
        return `<span class="coupon-discount-bar__row"><span aria-hidden="true">&#10003;</span>${escapeHTML(friendlyLabel(discount))}${code}${saved}</span>`;
      })
      .join('');
  }

  function buildFallbackHTML(bar) {
    const message = bar.dataset.fallbackMessage || '';
    const showCode = bar.dataset.showCode === 'true';
    const code = getCookie('discount_code');
    const codeHTML = showCode && code ? `<span class="coupon-discount-bar__code">${escapeHTML(baseCode(code))}</span>` : '';
    return `<span class="coupon-discount-bar__row"><span aria-hidden="true">&#10003;</span>${escapeHTML(message)}${codeHTML}</span>`;
  }

  function escapeHTML(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function isDismissed(signature) {
    return sessionStorage.getItem(STORAGE_KEY) === signature;
  }

  function dismiss(bar, signature) {
    sessionStorage.setItem(STORAGE_KEY, signature);
    bar.setAttribute('hidden', '');
  }

  function show(bar) {
    bar.removeAttribute('hidden');
  }

  function refresh(bar) {
    const content = bar.querySelector('[data-coupon-discount-bar-content]');
    if (!content) return;

    fetch(`${window.routes ? window.routes.cart_url : '/cart'}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const discounts = cart.cart_level_discount_applications || [];

        if (discounts.length > 0) {
          const signature = signatureFor(discounts);
          bar.dataset.discountSignature = signature;
          content.innerHTML = buildRowsHTML(bar, discounts);
          if (isDismissed(signature)) {
            bar.setAttribute('hidden', '');
          } else {
            show(bar);
          }
          return;
        }

        if (bar.dataset.onlyWithDiscount === 'false') {
          bar.dataset.discountSignature = 'preview';
          content.innerHTML = buildFallbackHTML(bar);
          show(bar);
          return;
        }

        const code = getCookie('discount_code');

        // A cart with items but no discount means Shopify already had its chance to resolve the
        // discount and it isn't there -- either it was never valid, or the customer removed it at
        // checkout. Either way, the discount_code cookie is stale at that point (Shopify doesn't
        // clear it on its own), so the fallback message has to stay off and the cookie gets cleared
        // so it can't cause this again on a later page. The fallback is only for a cart that's still
        // empty, i.e. genuinely hasn't had the chance to apply anything yet.
        if (cart.item_count > 0) {
          if (code) clearCookie('discount_code');
          bar.setAttribute('hidden', '');
          return;
        }

        if (code && !isDismissed(code)) {
          bar.dataset.discountSignature = code;
          content.innerHTML = buildFallbackHTML(bar);
          show(bar);
        } else {
          bar.setAttribute('hidden', '');
        }
      })
      .catch(() => {});
  }

  function init(bar) {
    // Inside the theme editor, Liquid already renders placeholder content so the section is never
    // blank while merchants edit it -- leave that alone instead of overwriting/hiding it with the
    // real (likely discount-less) cart state.
    if (window.Shopify && window.Shopify.designMode) return;

    const dismissButton = bar.querySelector('[data-coupon-discount-bar-dismiss]');
    if (dismissButton) {
      dismissButton.addEventListener('click', () => {
        dismiss(bar, bar.dataset.discountSignature || 'generic');
      });
    }

    // The bar may already be server-rendered with a live discount. Only hide it if the customer
    // already dismissed this exact set of discounts earlier in the session.
    if (!bar.hasAttribute('hidden') && bar.dataset.discountSignature && isDismissed(bar.dataset.discountSignature)) {
      bar.setAttribute('hidden', '');
      return;
    }

    // If the bar wasn't server-rendered visible (cart was empty at page load), attempt the
    // cookie-driven fallback, then keep it in sync as the cart changes.
    if (bar.hasAttribute('hidden')) {
      refresh(bar);
    }

    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => refresh(bar));
    }
  }

  function initAll() {
    document.querySelectorAll('[data-coupon-discount-bar]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
